import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
export const useProductPricingStore = defineStore('productPricing', () => {
    // State
    const priceEuro = ref(0);
    const priceCent = ref(0);
    const isLoading = ref(false);
    const error = ref(null);
    const breakdown = ref(null);
    const currentConfig = ref(null);
    // Getters
    const formattedPrice = computed(() => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(priceEuro.value);
    });
    const hasPrice = computed(() => priceCent.value > 0);
    // Actions
    let debounceTimer = null;
    let apiClient = null;
    const setApiClient = (client) => {
        apiClient = client;
    };
    const calculatePrice = async (config) => {
        if (!apiClient) {
            console.error('❌ [ProductPricingStore] API Client not set!');
            error.value = 'API Client not initialized';
            return;
        }
        // Clear previous debounce
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        // Debounce 300ms
        debounceTimer = setTimeout(async () => {
            isLoading.value = true;
            error.value = null;
            try {
                console.log('🎯 [ProductPricingStore] Calculating price for config:', config);
                // ✅ FIX: Use calculateProductPrice() method instead of .post()
                // ✅ FIX: Add null check for apiClient
                if (!apiClient) {
                    throw new Error('API Client not initialized');
                }
                // ✅ FIX: Remove 'productName' and 'basePriceCent' - not needed in request
                const response = await apiClient.calculateProductPrice({
                    productId: config.productId,
                    quantity: config.quantity,
                    selectedSizeId: config.selectedSizeId,
                    extraIngredients: config.extraIngredients || [],
                    removedIngredients: config.removedIngredients || []
                });
                if (response.status === 'success' && response.data) {
                    // ✅ FIX: Response structure is { data: { product, pricing } }
                    const { pricing } = response.data;
                    priceCent.value = pricing.lineItemTotalCent;
                    priceEuro.value = pricing.lineItemTotalCent / 100;
                    breakdown.value = pricing;
                    currentConfig.value = config;
                    console.log('✅ [ProductPricingStore] Price calculated:', {
                        priceCent: priceCent.value,
                        priceEuro: priceEuro.value,
                        formatted: formattedPrice.value
                    });
                }
                else {
                    throw new Error('Invalid response from server');
                }
            }
            catch (err) {
                console.error('❌ [ProductPricingStore] Price calculation failed:', err);
                error.value = err.message || 'Preisberechnung fehlgeschlagen';
                priceCent.value = 0;
                priceEuro.value = 0;
                breakdown.value = null;
            }
            finally {
                isLoading.value = false;
            }
        }, 300);
    };
    const updateConfig = (partialConfig) => {
        if (!currentConfig.value) {
            console.warn('⚠️ [ProductPricingStore] No current config to update');
            return;
        }
        const updatedConfig = {
            ...currentConfig.value,
            ...partialConfig
        };
        calculatePrice(updatedConfig);
    };
    const reset = () => {
        priceEuro.value = 0;
        priceCent.value = 0;
        isLoading.value = false;
        error.value = null;
        breakdown.value = null;
        currentConfig.value = null;
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }
        console.log('🔄 [ProductPricingStore] Store reset');
    };
    return {
        // State
        priceEuro,
        priceCent,
        isLoading,
        error,
        breakdown,
        currentConfig,
        // Getters
        formattedPrice,
        hasPrice,
        // Actions
        setApiClient,
        calculatePrice,
        updateConfig,
        reset
    };
});
